// React
import { ReactNode, Suspense, useState } from "react"

// Dates
import { DateTime } from "@/lib/dates"

// UI
import { getChildByType } from "@/lib/children"
import { useCurrentDate } from "@/hooks/useCurrentDate"
import { CardBody } from "../../../Card"
import { GraphHeaderPreloader } from "../../header/GraphHeader"
import { AreaGraphActions } from "./AreaGraphActions"
import { AreaGraphHeader } from "./AreaGraphHeader"
import { EmptyGridStateAnimated } from "../../../EmptyGridState"

// Hooks
import { useSunCalc } from "@/hooks/useSunCalc"

// Context
import { AreaGraphContext } from "./AreaGraphContext"

export function AreaGraphCardPreloader() {
	return (
		<CardBody>
			<div className="w-full lg:w-72">
				<GraphHeaderPreloader />
			</div>
			<div className="h-96 p-10">
				<div className="size-full animate-pulse bg-gray-50" />
			</div>
		</CardBody>
	)
}

interface AreaGraphProps {
	minimumDate?: DateTime
	maximumDate?: DateTime
	children: ReactNode
}

export function AreaGraph({
	// we do not store daily information for days more than a week old
	minimumDate = DateTime.local().minus({ weeks: 1 }).endOf("day"),
	maximumDate = DateTime.local().endOf("day"),
	children,
}: AreaGraphProps) {
	const content = getChildByType(children, AreaGraph.Content)
	const header = getChildByType(children, AreaGraph.Header)

	const now = useCurrentDate()
	const [date, setDate] = useState<Date>(maximumDate.toJSDate())

	const currentDateTime = DateTime.fromJSDate(date)

	const result = useSunCalc(date)
	const sunrise = result.sunrise!.getTime()
	const sunset = result.sunset!.getTime()

	const nowDateTime = DateTime.fromJSDate(now)

	// is currentDateTime (current selected date) before now?
	const isToday = currentDateTime.hasSame(nowDateTime, "day")
	const isBeforeSunrise = isToday && Number(date) < Number(sunrise)

	return (
		<AreaGraphContext.Provider
			value={{
				date,
				setDate,
				sunrise,
				sunset,
				isBeforeSunrise,
				isToday,
				maximumDate,
				minimumDate,
			}}
		>
			<CardBody
				className="lg:flex lg:items-center lg:justify-between"
				data-testid="production-daily"
			>
				<Suspense
					fallback={
						<GraphHeaderPreloader className="w-full lg:w-72" />
					}
				>
					{header ?? (
						<>
							<div>
								<AreaGraphHeader />
							</div>
							<div className="mt-4 flex lg:mt-0">
								<AreaGraphActions />
							</div>
						</>
					)}
				</Suspense>
			</CardBody>
			<div className="relative h-96">
				<Suspense
					fallback={
						<EmptyGridStateAnimated className="h-[30%] w-full" />
					}
				>
					{content}
				</Suspense>
			</div>
		</AreaGraphContext.Provider>
	)
}

// Define the type for components with displayName
type AreaGraphComponent = {
	({ children }: { children: ReactNode }): React.ReactElement
	displayName?: string
}

function Content({ children }: { children: ReactNode }) {
	return <>{children}</>
}
AreaGraph.Content = Content as AreaGraphComponent
AreaGraph.Content.displayName = "AreaGraph.Content"

function Header({ children }: { children: ReactNode }) {
	return <>{children}</>
}
AreaGraph.Header = Header as AreaGraphComponent
AreaGraph.Header.displayName = "AreaGraph.Header"
